import { createContext, useContext, type ReactNode } from 'react';
import { Platform, useColorScheme } from 'react-native';
import { useFonts } from 'expo-font';
import { VIDA_FONTS, VidaThemeProvider } from '@vidahealth/vida-ds';

import { DESIGN_SYSTEM_FLAG, getConfigFeatureFlag, isEmbedded } from '../config/runtimeConfig';
import type { Mode } from './ThemePalette';

const DesignSystemContext = createContext(false);

export const useDsEnabled = (): boolean => useContext(DesignSystemContext);

// RN can't drive variable font axes, so native swaps in the static Season Mix face (see vida-ds fonts.ts).
const DS_FONTS =
  Platform.OS === 'web'
    ? VIDA_FONTS
    : { ...VIDA_FONTS, 'Season Mix': require('@vidahealth/vida-ds/assets/fonts/SeasonMix-Medium.otf') };

// Standalone flips by deploy, with app.json's web.favicon, not PostHog: a flag landing after first paint would remount every screen.
const STANDALONE_ENABLED = false;

// Embedding hosts (VRN) load the fonts at their root.
const StandaloneFonts = ({ children }: { children: ReactNode }) => {
  const [fontsLoaded, fontError] = useFonts(DS_FONTS);
  if (!fontsLoaded && !fontError) return null;
  return children;
};

type DesignSystemProviderProps = {
  mode?: Mode;
  children: ReactNode;
};

export const DesignSystemProvider = ({ mode, children }: DesignSystemProviderProps) => {
  const embedded = isEmbedded();
  const colorScheme = useColorScheme();
  const configured = getConfigFeatureFlag(DESIGN_SYSTEM_FLAG);
  const enabled = embedded ? configured === true : (configured ?? STANDALONE_ENABLED);

  if (!enabled) {
    return <DesignSystemContext.Provider value={false}>{children}</DesignSystemContext.Provider>;
  }

  // Login themes itself under a host's provider too: the color scheme unless `mode` pins one, mobile scale.
  const themed = (
    <VidaThemeProvider mode={mode ?? (colorScheme === 'dark' ? 'dark' : 'light')} scale='mobile'>
      {children}
    </VidaThemeProvider>
  );

  return (
    <DesignSystemContext.Provider value={true}>
      {embedded ? themed : <StandaloneFonts>{themed}</StandaloneFonts>}
    </DesignSystemContext.Provider>
  );
};
